/**
 * useSolar composable
 *
 * Provides reactive sun and moon data from the hub's SolarService.
 *
 * - Fetches the initial snapshot from GET /api/solar/current on mount.
 * - Subscribes to `solar:update` Socket.IO events (emitted by the backend
 *   every minute) to stay up-to-date without polling.
 * - Exposes helpers for formatting times, phase icons, and sky positioning
 *   for use in SolarCard and other visualisation components.
 *
 * Usage:
 * ```ts
 * const { snapshot, sunPosition, moonIllumination, moonPhaseEmoji, loading } = useSolar();
 * ```
 */

import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { formatTime as formatTimeLocale } from '../utils/formatDate';
import { apiFetch } from '../services/api';
import { useSocketStore } from '../stores/socket';

// ---------------------------------------------------------------------------
// Types (mirror SolarService types — no shared package yet)
// ---------------------------------------------------------------------------

export interface SunTimes {
  dawn: string;
  sunrise: string;
  goldenHourMorningEnd: string;
  solarNoon: string;
  goldenHourEveningStart: string;
  sunset: string;
  dusk: string;
  nadir: string;
}

export interface SunPosition {
  azimuth: number;
  altitude: number;
  isDay: boolean;
}

export interface MoonTimes {
  rise: string | null;
  set: string | null;
  alwaysUp: boolean;
  alwaysDown: boolean;
}

export interface MoonPosition {
  azimuth: number;
  altitude: number;
  distance: number;
  isAboveHorizon: boolean;
}

export interface MoonIllumination {
  fraction: number;
  phase: number;
  phaseName: string;
  angle: number;
}

export interface SolarSnapshot {
  sun: {
    times: SunTimes;
    position: SunPosition;
  };
  moon: {
    times: MoonTimes;
    position: MoonPosition;
    illumination: MoonIllumination;
  };
  location: { latitude: number; longitude: number };
  calculatedAt: string;
}

// ---------------------------------------------------------------------------
// Moon phase → emoji map
// ---------------------------------------------------------------------------

const MOON_PHASE_EMOJI: Record<string, string> = {
  'New Moon': '🌑',
  'Waxing Crescent': '🌒',
  'First Quarter': '🌓',
  'Waxing Gibbous': '🌔',
  'Full Moon': '🌕',
  'Waning Gibbous': '🌖',
  'Last Quarter': '🌗',
  'Waning Crescent': '🌘',
};

// ---------------------------------------------------------------------------
// Shared singleton state
//
// useSolar() was previously a fully isolated composable: every SwitchWidget /
// LightWidget / SolarCard instance fetched GET /api/solar/current on mount and
// registered its own `solar:update` socket listener. With dozens of device
// buttons per page that produced one redundant API request per widget
// (see the /api/solar/current storm in the devices view logs).
//
// The state now lives at module scope and is shared by every useSolar() call:
// one fetch, one socket listener, many consumers. Consumers are ref-counted so
// the socket listener is detached when the last component unmounts, and
// re-attached (plus a refetch) when the first one mounts again.
// ---------------------------------------------------------------------------

const snapshot = ref<SolarSnapshot | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

// ---------------------------------------------------------------------------
// Shared computed helpers
// ---------------------------------------------------------------------------

/** Current sun position (azimuth / altitude / isDay) */
const sunPosition = computed<SunPosition | null>(() => snapshot.value?.sun.position ?? null);

/** Today's key sun times */
const sunTimes = computed<SunTimes | null>(() => snapshot.value?.sun.times ?? null);

/** Current moon illumination / phase */
const moonIllumination = computed<MoonIllumination | null>(() => snapshot.value?.moon.illumination ?? null);

/** Current moon position */
const moonPosition = computed<MoonPosition | null>(() => snapshot.value?.moon.position ?? null);

/** Emoji representing the current moon phase */
const moonPhaseEmoji = computed<string>(() => {
  const name = snapshot.value?.moon.illumination.phaseName;
  return name ? (MOON_PHASE_EMOJI[name] ?? '🌙') : '🌙';
});

/** Illumination percentage (0–100) */
const moonIlluminationPercent = computed<number>(() =>
  Math.round((snapshot.value?.moon.illumination.fraction ?? 0) * 100),
);

/**
 * Sun progress across the sky arc as 0–1
 * 0 = at sunrise, 1 = at sunset
 */
const sunArcProgress = computed<number>(() => {
  const times = snapshot.value?.sun.times;
  if (!times) return 0;

  const now = Date.now();
  const rise = new Date(times.sunrise).getTime();
  const set = new Date(times.sunset).getTime();

  if (now <= rise) return 0;
  if (now >= set) return 1;
  return (now - rise) / (set - rise);
});

/** True when it's currently daytime */
const isDay = computed<boolean>(() => snapshot.value?.sun.position.isDay ?? false);

// ---------------------------------------------------------------------------
// Shared fetching (one request for the whole app)
// ---------------------------------------------------------------------------

let fetchPromise: Promise<void> | null = null;
let lastFetchFailed = false;
let lastFetchedAt = 0;

async function fetchSnapshot(): Promise<void> {
  // Deduplicate: concurrent consumers share the in-flight request.
  if (fetchPromise) return fetchPromise;

  fetchPromise = (async () => {
    try {
      loading.value = true;
      error.value = null;
      snapshot.value = await apiFetch('/solar/current');
      lastFetchFailed = false;
      lastFetchedAt = Date.now();
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err?.message ?? 'Failed to load solar data';
      lastFetchFailed = true;
      console.error('[useSolar] Failed to fetch snapshot:', err);
    } finally {
      loading.value = false;
    }
  })().finally(() => {
    fetchPromise = null;
  });

  return fetchPromise;
}

// ---------------------------------------------------------------------------
// Shared socket subscription (one listener for the whole app)
// ---------------------------------------------------------------------------

function handleSolarUpdate(data: SolarSnapshot) {
  snapshot.value = data;
  // Live pushes count as fresh data — avoids a needless refetch on re-entry.
  lastFetchFailed = false;
  lastFetchedAt = Date.now();
}

let consumerCount = 0;
let boundSocket: ReturnType<typeof useSocketStore>['socket'] = null;
let stopSocketWatch: (() => void) | null = null;

function detachSolarListener() {
  if (boundSocket) {
    boundSocket.off('solar:update', handleSolarUpdate);
    boundSocket = null;
  }
}

function attachSolarListener() {
  const socketStore = useSocketStore();
  const current = socketStore.socket ?? null;
  if (current === boundSocket) return;

  detachSolarListener();
  boundSocket = current;
  if (boundSocket) {
    boundSocket.on('solar:update', handleSolarUpdate);
  }
}

function ensureSolarStarted() {
  // First consumer mounts the global socket watcher: the socket instance can
  // change on reconnects (and in cloud mode connects only after the WebRTC
  // channel is up), so re-attach whenever the instance changes.
  if (!stopSocketWatch) {
    attachSolarListener();
    stopSocketWatch = watch(
      () => useSocketStore().socket,
      () => {
        attachSolarListener();
        // Retry the initial fetch when a socket appears after a failure —
        // e.g. cloud mode, where the first fetch ran before the WebRTC
        // channel to the hub existed.
        if (lastFetchFailed) {
          void fetchSnapshot();
        }
      },
    );
  }

  // Refresh on (re)entry: the first consumer of a page triggers one shared
  // fetch; every other widget instance reuses the same state. Refetch when
  // data is missing, the last attempt failed, or the snapshot is over a
  // minute old (re-attaching the socket listener would otherwise leave a
  // stale view until the next solar:update push).
  if (!snapshot.value || lastFetchFailed || Date.now() - lastFetchedAt > 60_000) {
    void fetchSnapshot();
  }
}

function stopSolarIfUnused() {
  consumerCount = Math.max(0, consumerCount - 1);
  if (consumerCount > 0) return;

  stopSocketWatch?.();
  stopSocketWatch = null;
  detachSolarListener();
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useSolar() {
  onMounted(() => {
    consumerCount += 1;
    ensureSolarStarted();
  });

  onUnmounted(() => {
    stopSolarIfUnused();
  });

  /** Format a Date/ISO string to "HH:MM" local time */
  function formatTime(dateStr: string | null | undefined): string {
    if (!dateStr) return '—';
    return formatTimeLocale(new Date(dateStr), {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return {
    // State
    snapshot,
    loading,
    error,

    // Computed
    sunPosition,
    sunTimes,
    moonIllumination,
    moonPosition,
    moonPhaseEmoji,
    moonIlluminationPercent,
    sunArcProgress,
    isDay,

    // Actions
    fetchSnapshot,

    // Utilities
    formatTime,
  };
}
